import { verisoul } from '../api/verisoul/verisoul';
import { useCallback } from 'react';
import { UserActivityType } from 'shared/enums/user-activity-type/user-activity-type.enums';
import i18n from 'src/i18n';

interface UseVerisoulReturn {
  authenticateSession: (userActivityType: UserActivityType, sessionId?: string) => Promise<void>;
  getSessionId: () => Promise<string | null>;
  authenticateCurrentSession: (userActivityType: UserActivityType) => Promise<boolean>;
  reinitializeSession: () => Promise<void>;
}

export function useVerisoul(): UseVerisoulReturn {
  const authenticateSession = useCallback(async (userActivityType: UserActivityType, sessionId?: string) => {
    try {
      await verisoul.authenticateSession(userActivityType, sessionId);
    } catch (e) {
      throw new Error(i18n.t('HOOKS.VERISOUL.AUTHENTICATE_FAILED'));
    }
  }, []);

  const getSessionId = useCallback(async (): Promise<string | null> => {
    const maxAttempts = 3;
    const delayMs = 250;
    const totalTimeoutMs = 2000;
    const startTime = Date.now();

    for (let attemptIndex = 0; attemptIndex < maxAttempts; attemptIndex += 1) {
      try {
        const timeoutPromise = new Promise<{ session_id?: string }>((resolve) =>
          setTimeout(() => resolve({ session_id: undefined }), Math.max(0, totalTimeoutMs - (Date.now() - startTime)))
        );
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { session_id } =
          (await Promise.race([window.Verisoul.session(), timeoutPromise])) ?? ({} as { session_id?: string });
        if (session_id) return session_id;
      } catch {
        // ignore and retry below
      }
      // Stop retrying if we've exceeded the total timeout budget
      if (Date.now() - startTime >= totalTimeoutMs) {
        return null;
      }
      if (attemptIndex < maxAttempts - 1) {
        await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
      }
    }
    return null;
  }, []);

  const authenticateCurrentSession = useCallback(
    async (userActivityType: UserActivityType): Promise<boolean> => {
      const sessionId = await getSessionId();
      if (!sessionId) return false;
      await authenticateSession(userActivityType, sessionId);
      return true;
    },
    [getSessionId, authenticateSession]
  );

  const reinitializeSession = useCallback(async () => {
    const totalTimeoutMs = 2000;
    const timeoutPromise = new Promise<void>((resolve) => setTimeout(resolve, totalTimeoutMs));
    try {
      await Promise.race([window.Verisoul.reinitialize(), timeoutPromise]);
    } catch {
      // ignore
    }
  }, []);

  return {
    authenticateSession,
    getSessionId,
    authenticateCurrentSession,
    reinitializeSession,
  };
}
